'use client';

import {
  Children,
  type ReactElement,
  type ReactNode,
  cloneElement,
  isValidElement,
  useContext,
  useMemo,
} from 'react';

import { TableRootContext } from '../context/table-root.context';

/**
 * A hook that aligns table cells based on column configuration.
 *
 * Takes an array of children (table cells) and applies alignment props based on the
 * `alignments` array from TableRootContext. The hook intelligently handles cells with
 * `colspan`, ensuring the alignment index advances correctly for merged cells.
 */
export const useAlignedCells = (props: { children: ReactNode }) => {
  const { children } = props;

  // Get alignment configuration from the table context
  const { alignments } = useContext(TableRootContext);

  return useMemo(() => {
    // Track the current column index for alignment mapping
    let currentIndex = 0;

    return Children.map(children, (child) => {
      // Skip invalid React elements (non-elements, nulls, strings, etc.)
      if (!isValidElement(child)) return null;

      // Get alignment for current column, fallback to 'start' if not specified
      const alignment = alignments?.[currentIndex] || 'start';

      // Handle colspan by getting the span value or defaulting to 1
      const colspan = Number(child.props.colSpan) || 1;

      // Advance the index by colspan to maintain correct alignment mapping
      // oxlint-disable-next-line react/immutability -- The accumulator is local to this synchronous map.
      currentIndex += colspan;

      // Clone the cell element with the calculated alignment prop
      return cloneElement(child as ReactElement, {
        align: alignment,
      });
    });
  }, [alignments, children]);
};
